/**
 * Utilities for building and filtering the "Changed Column" dropdown options
 * in Workflow / Sequence / Document-Wizard condition modals.
 *
 * Each option has shape: { code: string, name: string, type: string, group: string }
 * `code` and `name` are the token string (e.g. "Company Name").
 * `group` is the human-readable section header shown in the grouped Autocomplete.
 *
 * MUI Autocomplete `groupBy` requires all items of the same group to be contiguous,
 * so we concat groups in a deterministic order and never intermix them.
 */

export const GROUP_LABELS = {
  document: "Document",
  company: "Company",
  contact: "Contact",
  organization: "Organization Rep",
  opportunity: "Opportunity",
  task: "Task"
};

/**
 * Which field groups appear in the Changed Column dropdown for each workflow trigger.
 * Order is preserved in the rendered list.
 */
export const WORKFLOW_MODULE_GROUP_KEYS = {
  company: ["company", "organization"],
  contact: ["company", "contact", "organization"],
  task: ["company", "contact", "organization", "document", "task", "opportunity"],
  opportunity: ["company", "opportunity", "contact", "document", "organization"],
  document: ["document", "organization", "opportunity"]
};

/**
 * Returns a human-readable group label for a module key.
 * Falls back to the capitalized module key for unknown keys.
 */
function groupLabel(moduleKey) {
  return GROUP_LABELS[moduleKey] || `${moduleKey.charAt(0).toUpperCase()}${moduleKey.slice(1)}`;
}

/**
 * Tags a raw options array with a `group` property.
 * Returns an empty array when `items` is falsy or empty.
 */
function tagWithGroup(items, moduleKey) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const label = groupLabel(moduleKey);
  return items.map((item) => ({ ...item, group: label }));
}

/**
 * Builds the flat, grouped options array for the "Changed Column" Autocomplete.
 *
 * Uses `WORKFLOW_MODULE_GROUP_KEYS` to determine which groups are included for
 * each trigger. Groups with no API data are omitted. Unknown triggers fall back
 * to that trigger's own fields only.
 *
 * `data` is the Redux `workflowCondition.data` object, which may be an empty
 * array `[]` on first render (before the API response arrives).
 *
 * @param {object|array} data - grouped condition tokens from the API
 * @param {string} moduleValue - workflow trigger key (e.g. "company", "task")
 * @param {object} [groupKeysMap=WORKFLOW_MODULE_GROUP_KEYS] - optional override map
 */
export function buildChangedColumnOptions(data, moduleValue, groupKeysMap = WORKFLOW_MODULE_GROUP_KEYS) {
  if (!data || Array.isArray(data) || !moduleValue) return [];

  const groupKeys = groupKeysMap[moduleValue] ?? [moduleValue];

  return groupKeys.flatMap((key) => tagWithGroup(data[key], key));
}

/**
 * Filters the options array by a search query using case-insensitive substring
 * matching on `name`. Returns all options when query is empty/whitespace.
 *
 * Used as the Autocomplete `filterOptions` prop so the behaviour is isolated
 * and independently unit-testable.
 */
export function filterChangedColumnOptions(options, query) {
  if (!query || !query.trim()) return options;
  const lower = query.trim().toLowerCase();
  return options.filter((option) => option.name.toLowerCase().includes(lower));
}
